import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { MessageSquare, Users, Clock, Download, Filter, ChevronDown, ChevronRight, NotebookPen } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
  short_text: "Short",
  long_text: "Text",
  true_false: "T/F",
  ranking: "Rank",
  fill_blank: "Blank",
};

const AnswerViewer = ({ sessionId, stations, compact = false }) => {
  const [answers, setAnswers] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStation, setSelectedStation] = useState("all");
  const [groupFilter, setGroupFilter] = useState("all");
  const [expandedQ, setExpandedQ] = useState({});

  useEffect(() => {
    fetchAnswers();
  }, [sessionId]);

  const fetchAnswers = async () => {
    setLoading(true);
    try {
      // Fetch answers for all stations
      const allAnswers = [];
      for (const station of stations) {
        const res = await axios.get(`${API}/sessions/${sessionId}/stations/${station.id}/answers`);
        allAnswers.push(...(res.data.answers || []).map(a => ({
          ...a,
          station_name: station.name,
          station_index: station.order_index ?? stations.indexOf(station),
        })));
      }
      setAnswers(allAnswers);
      // Fetch station observation notes (best effort)
      try {
        const notesRes = await axios.get(`${API}/sessions/${sessionId}/station-notes`);
        setNotes(notesRes.data?.notes || []);
      } catch (e) {
        console.warn("Could not load station notes:", e);
      }
    } catch (err) {
      console.error("Failed to fetch answers:", err);
    } finally {
      setLoading(false);
    }
  };

  // Filter
  const filtered = answers.filter(a => {
    if (selectedStation !== "all" && a.station_id !== selectedStation) return false;
    if (groupFilter !== "all" && String(a.group_number) !== groupFilter) return false;
    return true;
  });

  // Group answers by station > question
  const grouped = {};
  for (const a of filtered) {
    const sKey = a.station_id;
    if (!grouped[sKey]) grouped[sKey] = { name: a.station_name, questions: {} };
    const qKey = a.question_id;
    if (!grouped[sKey].questions[qKey]) grouped[sKey].questions[qKey] = { id: qKey, answers: [] };
    grouped[sKey].questions[qKey].answers.push(a);
  }

  // Get unique groups
  const groups = [...new Set(answers.map(a => a.group_number))].sort((a, b) => a - b);

  // Find question text from stations
  const getQuestionText = (questionId) => {
    for (const station of stations) {
      for (const q of (station.questions || [])) {
        if (q.id === questionId) return q.text;
        for (const p of (q.parts || [])) {
          if (p.id === questionId) return `${q.text} (${p.label})`;
          if (`${q.id}_${p.id}` === questionId) return `${q.text} → ${p.text}`;
        }
      }
    }
    return questionId;
  };

  const exportCSV = () => {
    if (filtered.length === 0) { toast.error("No answers to export"); return; }
    const headers = ["Station", "Question", "Participant", "Group", "Answer", "Time"];
    const rows = filtered.map(a => [
      a.station_name,
      getQuestionText(a.question_id),
      a.participant_name || "Anonymous",
      `Team ${a.group_number}`,
      `"${(a.answer_text || "").replace(/"/g, '""')}"`,
      new Date(a.answered_at).toLocaleString(),
    ]);
    const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `answers-${sessionId.slice(0, 8)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("Answers exported as CSV!");
  };

  const toggleQ = (key) => setExpandedQ(prev => ({ ...prev, [key]: !prev[key] }));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin w-8 h-8 border-3 border-[#E91E8C] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="answer-viewer">
      {/* Header & Filters */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-[#E91E8C]" />
          <h3 className="text-lg font-semibold text-white">{filtered.length} Answers</h3>
        </div>
        <div className="flex items-center gap-2">
          {/* Station filter */}
          <select
            value={selectedStation}
            onChange={(e) => setSelectedStation(e.target.value)}
            className="text-sm rounded-lg bg-white/5 border border-white/10 text-white px-3 py-1.5 focus:outline-none focus:border-[#E91E8C]/50"
            data-testid="station-filter"
          >
            <option value="all">All Stations</option>
            {stations.map((s, i) => (
              <option key={s.id} value={s.id}>Station {i + 1}: {s.name}</option>
            ))}
          </select>

          {/* Group filter */}
          <select
            value={groupFilter}
            onChange={(e) => setGroupFilter(e.target.value)}
            className="text-sm rounded-lg bg-white/5 border border-white/10 text-white px-3 py-1.5 focus:outline-none focus:border-[#E91E8C]/50"
            data-testid="group-filter"
          >
            <option value="all">All Teams</option>
            {groups.map(g => (
              <option key={g} value={String(g)}>Team {g}</option>
            ))}
          </select>

          <Button onClick={exportCSV} variant="outline" size="sm" className="btn-ghost" data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-1" /> CSV
          </Button>

          <Button onClick={fetchAnswers} variant="ghost" size="sm" className="text-[#6B5E7C] hover:text-white">
            <Clock className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* No answers */}
      {filtered.length === 0 && notes.length === 0 && (
        <div className="text-center py-8 text-[#6B5E7C]">
          <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No answers yet</p>
        </div>
      )}

      {/* Grouped answers + observation notes (iterate stations so notes-only stations also appear) */}
      {stations
        .filter((s) => selectedStation === "all" || s.id === selectedStation)
        .map((station) => {
          const stationId = station.id;
          const stationData = grouped[stationId] || { name: station.name, questions: {} };
          const stationNotes = notes.filter((n) =>
            n.station_id === stationId &&
            (groupFilter === "all" || String(n.group_number) === groupFilter)
          );
          const hasQuestions = Object.keys(stationData.questions).length > 0;
          if (!hasQuestions && stationNotes.length === 0) return null;
          return (
        <div key={stationId} className="space-y-2">
          {selectedStation === "all" && (
            <h4 className="text-sm font-semibold text-[#E2D9F3] flex items-center gap-2 mt-2">
              <span className="w-2 h-2 rounded-full bg-[#E91E8C]" />
              {stationData.name}
            </h4>
          )}

          {Object.entries(stationData.questions).map(([qId, qData]) => {
            const qKey = `${stationId}_${qId}`;
            const isExpanded = expandedQ[qKey] !== false; // Default expanded
            const qText = getQuestionText(qId);

            return (
              <div key={qKey} className="glass-light rounded-xl overflow-hidden">
                {/* Question header */}
                <button
                  onClick={() => toggleQ(qKey)}
                  className="w-full flex items-center justify-between p-3 hover:bg-white/5 transition-colors text-left"
                  data-testid={`question-header-${qId}`}
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {isExpanded ? <ChevronDown className="w-4 h-4 text-[#6B5E7C] flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-[#6B5E7C] flex-shrink-0" />}
                    <p className="text-sm text-white font-medium truncate">{qText}</p>
                  </div>
                  <span className="text-xs text-[#6B5E7C] ml-2 flex-shrink-0 bg-white/5 px-2 py-0.5 rounded-full">
                    {qData.answers.length} {qData.answers.length === 1 ? 'answer' : 'answers'}
                  </span>
                </button>

                {/* Answer rows */}
                {isExpanded && (
                  <div className="border-t border-white/5">
                    {qData.answers.map((answer, idx) => (
                      <motion.div
                        key={answer.answered_at + idx}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="flex items-start gap-3 px-4 py-2.5 border-b border-white/5 last:border-b-0 hover:bg-white/5"
                      >
                        <div className="flex-shrink-0 mt-0.5">
                          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold bg-[#8B5CF6]/20 text-[#8B5CF6]">
                            {answer.group_number}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm leading-relaxed break-words">{answer.answer_text}</p>
                          <p className="text-[#6B5E7C] text-xs mt-0.5">
                            {answer.participant_name || "Anonymous"} · {new Date(answer.answered_at).toLocaleTimeString()}
                          </p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* Observation notes for this station */}
          {stationNotes.length > 0 && (
            <div className="glass-light rounded-xl p-3 border-l-2 border-[#FFB74D]" data-testid={`station-notes-block-${stationId}`}>
              <p className="text-xs uppercase tracking-wide text-[#FFB74D] font-semibold flex items-center gap-1.5 mb-2">
                <NotebookPen className="w-3 h-3" /> Observation notes
              </p>
              <div className="space-y-2">
                {stationNotes.map((n) => (
                  <div key={n.id} className="text-sm" data-testid={`station-note-${n.id}`}>
                    <p className="text-white whitespace-pre-wrap leading-relaxed">{n.note_text}</p>
                    <p className="text-[10px] text-[#6B5E7C] mt-0.5">
                      Team {n.group_number}{n.participant_name ? ` · ${n.participant_name}` : " · whole team"} · {n.author || "Host"} · {new Date(n.updated_at).toLocaleTimeString()}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        );
      })}
    </div>
  );
};

export default AnswerViewer;
